import { useMemo, useRef, useState } from "react";
import { Upload } from "lucide-react";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { decodeImage } from "@/components/image-crop/exportCroppedImage";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import type { ClubhouseAdKitImageDto } from "entities/responses/adkitImage";
import {
  useGetFacilityImagesQuery,
  useUploadAdKitImagesMutation,
} from "../../api/endpoints/images";
import type { PlacementCatalogEntry } from "../../placementCatalog";
import { ImagePreview } from "../library/ImageCard";
import {
  imageDisplayName,
  imageFitsPlacement,
  ratioFitsPlacement,
} from "../library/imageLibraryViewModel";
import {
  ACCEPTED_UPLOAD_EXTENSIONS,
  uploadRejection,
} from "../library/imageUpload";
import {
  PlacementCropDialog,
  type CropSource,
} from "../library/PlacementCropDialog";
import { CAN_CROP_LIBRARY_IMAGES } from "../library/placementCrop";

interface ImagePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placement: PlacementCatalogEntry;
  /** Images already on this placement — their button reads "Added". */
  addedIds: string[];
  onAdd: (image: ClubhouseAdKitImageDto) => void;
}

export function ImagePickerDialog({
  open,
  onOpenChange,
  placement,
  addedIds,
  onAdd,
}: ImagePickerDialogProps) {
  const facilityId = useGetCurrentLocalFacilityId();
  // Same args as the Image Library, so the two share one cache entry.
  const { data: allImages = [], isLoading } = useGetFacilityImagesQuery(
    { facilityId, includeDeletedImages: true },
    { skip: !facilityId || !open },
  );
  const [upload, { isLoading: isUploading }] = useUploadAdKitImagesMutation();
  const [cropSource, setCropSource] = useState<CropSource | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const images = useMemo(
    () => allImages.filter((image) => !image.deleted),
    [allImages],
  );
  const added = new Set(addedIds);

  const uploadFile = async (file: File) => {
    const rejection = uploadRejection(file);
    if (rejection) {
      toast.error("Use a PNG or JPG up to 5 MB.");
      return;
    }
    let fits = false;
    try {
      const bitmap = await decodeImage(file);
      fits = ratioFitsPlacement(bitmap.width / bitmap.height, placement);
    } catch {
      toast.error("We couldn't open that image. Try another file.");
      return;
    }
    // A file that doesn't fit is cropped before it's saved, so nothing stretches.
    if (!fits) {
      setCropSource({ kind: "file", file, name: file.name });
      return;
    }
    try {
      const response = await upload({
        facilityId,
        files: [{ file, imageName: file.name }],
      }).unwrap();
      const created = response.uploadedImages?.[0];
      if (!created) throw new Error("no image");
      onAdd(created);
    } catch (err) {
      const status = (err as { status?: number }).status;
      toast.error(
        status === 409
          ? `An image called ${file.name} is already in your library. Add it from the list.`
          : "Couldn't upload that image. Please try again.",
      );
    }
  };

  return (
    <>
      <Dialog open={open && cropSource === null} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-215">
          <DialogHeader>
            <DialogTitle>Add images to {placement.name}</DialogTitle>
            <DialogDescription>
              {placement.imageSize}. Images that don&rsquo;t fit are cropped
              first, so nothing looks stretched.
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <button
                type="button"
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
                className="flex min-h-33 flex-col items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-border p-3 text-center hover:border-ink/40 disabled:opacity-50"
              >
                <Upload className="size-5 text-ink" aria-hidden="true" />
                <span className="text-sm font-semibold text-ink">
                  {isUploading ? "Uploading…" : "Upload images"}
                </span>
                <span className="text-xs text-text-muted">
                  PNG or JPG, up to 5 MB. You&rsquo;ll crop each one to fit.
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_UPLOAD_EXTENSIONS}
                className="hidden"
                aria-label="Choose an image to upload"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) void uploadFile(file);
                }}
              />

              {isLoading && (
                <p className="col-span-full text-sm text-text-muted">
                  Loading your library…
                </p>
              )}

              {images.map((image) => {
                const fits = imageFitsPlacement(image, placement);
                const isAdded = added.has(String(image.id));
                const canCrop = CAN_CROP_LIBRARY_IMAGES;
                return (
                  <div
                    key={String(image.id)}
                    className="flex flex-col gap-1.5 rounded-xl border border-border p-2.5"
                  >
                    <ImagePreview image={image} className="h-18" />
                    <div className="truncate text-xs font-semibold text-ink">
                      {imageDisplayName(image)}
                    </div>
                    <div className="flex items-center justify-between gap-1.5">
                      <span
                        className={cn(
                          "rounded-md px-1.5 py-0.5 text-[11px] font-semibold",
                          fits
                            ? "bg-good-soft text-good"
                            : "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300",
                        )}
                      >
                        {fits ? "Fits" : "Needs crop"}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-lg px-2 text-xs"
                        disabled={isAdded || (!fits && !canCrop)}
                        title={
                          !fits && !canCrop && !isAdded
                            ? "Waiting on image storage access (CORS). Upload the original to crop it for now."
                            : undefined
                        }
                        onClick={() =>
                          fits
                            ? onAdd(image)
                            : setCropSource({ kind: "library", image })
                        }
                      >
                        {isAdded ? "Added" : fits ? "Add" : "Crop & add"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter className="items-center sm:justify-between">
            <span className="text-xs text-text-muted">
              Images that don&rsquo;t fit open the cropper first.
            </span>
            <Button className="rounded-lg" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlacementCropDialog
        source={cropSource}
        placement={cropSource ? placement : null}
        onCancel={() => setCropSource(null)}
        onCropped={(image) => {
          onAdd(image);
          setCropSource(null);
        }}
      />
    </>
  );
}
